import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Destination } from 'components/common/ObjectTypes';
import { PendingEdits } from 'components/common/configPanel/UnsavedSectionPrompt';
import {
  buttonClasses,
  formClasses,
  toggleClasses,
} from 'components/common/tailwindClassnames';
import {
  FieldErrors,
  FieldMapping,
  Rule,
  StreamType,
  buildConfigWithRule,
} from './fieldMappingTypes';
import FieldMappingForm from './FieldMappingForm';
import FieldMappingCodeEditor from './FieldMappingCodeEditor';
import SourceFiltersForm from 'components/routes/sourceFilters/SourceFiltersForm';
import {
  SourceFilter,
  SourceFilterDraft,
  SourceSummary,
  draftFromFilter,
  emptyDraft,
} from 'components/routes/sourceFilters/sourceFilterTypes';
import ModalShell from 'components/common/ModalShell';
import { useIsResizing } from 'components/common/useIsResizing';
import { useHelp } from 'context/HelpContext';
import { toast } from 'react-toastify';

type Mode = 'builder' | 'code';

// The panel surfaces both client-side validation (required fields, etc.) and any
// server-side field errors returned from a 400. The shape is shared with the parent
// so it can populate this map from a parsed backend response.
type FormErrors = FieldErrors;

type TransformationRulesPanelProps = {
  isOpen: boolean;
  onClose: () => void;
  destination: Destination | null;
  providerId: string;
  currentRule: Rule | null;
  // Persisted route_configuration name, or "" when no configuration exists yet.
  currentConfigurationName: string;
  // onSave may resolve with field-level errors when the backend rejects the payload.
  // Returning void / undefined is treated as success.
  onSave: (
    streamType: StreamType,
    mapping: FieldMapping,
    name: string,
  ) => Promise<FieldErrors | void> | FieldErrors | void;
  onDelete: () => Promise<void> | void;
  // Source filters (GUNDI-5178). Stored per route → destination pair, alongside
  // the field mapping rather than instead of it.
  currentSourceFilter?: SourceFilter | null;
  onSaveSourceFilter?: (draft: SourceFilterDraft) => Promise<void> | void;
  onDeleteSourceFilter?: () => Promise<void> | void;
  // The rule's devices are paged, so the editor asks for them only when it opens
  // rather than carrying them in the rule the summary card renders from.
  onLoadSourceFilterSources?: (filterId: string) => Promise<SourceSummary[]>;
  // Lands the panel straight in that rule's editor on open — set when the user
  // clicked Add or Edit from that rule's own section, which already says which
  // kind of rule they want.
  initialEditor?: 'device' | 'field_mapping' | null;
  // Bumped by the parent per editor request, so the intent lands even when the
  // panel is already open and isOpen has no rising edge to fire on.
  initialEditorNonce?: number;
  // Kept current with the open editor's unsaved edits, so the page can ask
  // before a tab switch or a link out of it throws them away.
  pendingEditsRef?: React.MutableRefObject<PendingEdits | null>;
};

function deriveDefaultName(
  providerId: string,
  destinationField: string,
): string {
  return `${providerId} ${destinationField || ''}`.trim();
}

const emptyMapping: FieldMapping = {
  map: {},
  default: '',
  provider_field: '',
  destination_field: '',
};

const segmentActive = `${toggleClasses.option} ${toggleClasses.active}`;
const segmentInactive = `${toggleClasses.option} ${toggleClasses.inactive}`;

function buildJsonPreview(
  providerId: string,
  destinationId: string,
  streamType: StreamType,
  mapping: FieldMapping,
): string {
  const config = buildConfigWithRule(
    {},
    providerId,
    destinationId,
    streamType,
    mapping,
  );
  return JSON.stringify(config, null, 2);
}

const TransformationRulesPanel = ({
  isOpen,
  onClose,
  destination,
  providerId,
  currentRule,
  currentConfigurationName,
  onSave,
  onDelete,
  currentSourceFilter = null,
  onSaveSourceFilter,
  onDeleteSourceFilter,
  onLoadSourceFilterSources,
  initialEditor = null,
  initialEditorNonce = 0,
  pendingEditsRef,
}: TransformationRulesPanelProps) => {
  const { t } = useTranslation();
  // While resizing, drop the slide transition so the width switch at the md breakpoint
  // doesn't animate the closed (translate-x-full) panel across the viewport.
  const isResizing = useIsResizing();
  const [showForm, setShowForm] = useState(false);
  const [streamType, setStreamType] = useState<StreamType>('ev');
  const [mapping, setMapping] = useState<FieldMapping>(emptyMapping);
  const [mode, setMode] = useState<Mode>('builder');
  const [code, setCode] = useState<string>('');
  const [codeValid, setCodeValid] = useState<Rule | null>(null);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  // Source-filter editing runs beside the field-mapping form: its own draft, its
  // own footer, and a wider panel while it is open.
  const [showDeviceForm, setShowDeviceForm] = useState(false);
  const [deviceDraft, setDeviceDraft] =
    useState<SourceFilterDraft>(emptyDraft());
  const [deviceSnapshot, setDeviceSnapshot] = useState<string>('');
  const [loadingSources, setLoadingSources] = useState(false);
  // Deleting a device rule releases the provider: every device, including new
  // ones, reaches the destination again. That consequence is confirmed, not
  // implied by a button label.
  const [confirmDeleteRule, setConfirmDeleteRule] = useState(false);
  const { setFabSuppressed } = useHelp();
  useEffect(() => {
    // The floating "?" floats exactly where this footer's Save sits.
    setFabSuppressed(isOpen && showDeviceForm);
    return () => setFabSuppressed(false);
  }, [isOpen, showDeviceForm, setFabSuppressed]);

  // Increments whenever the panel (re)opens for a rule, so the form / code editor can
  // be keyed off it and unmount any local UI state (like the "Add value mapping"
  // checkbox) that would otherwise leak between destinations.
  const [resetTick, setResetTick] = useState(0);
  // Editable name for the route_configuration. Defaults to "<providerId> <destination_field>"
  // and auto-syncs as the user fills the destination_field, unless they manually edit it.
  const [configName, setConfigName] = useState('');
  const [nameWasEdited, setNameWasEdited] = useState(false);
  // Captured when the form opens; Save stays disabled until something changes against it
  // so a no-op click can't trigger a duplicate route_configuration on the backend.
  const [snapshot, setSnapshot] = useState<{
    streamType: StreamType;
    mapping: string;
    configName: string;
  } | null>(null);

  const destinationId = destination?.id ?? '';

  // Invalidates device-list loads still in flight: a response that lands after the
  // editor closed or moved to another destination must not write the draft, or the
  // next save would replace THAT rule's device list with this one's.
  const deviceLoadSeq = useRef(0);

  // Closing or retargeting the panel discards the draft outright — otherwise a
  // dirty draft from destination A stays saveable while the save callback
  // already targets destination B.
  // Whoever bumps the generation owns the loading flag: an abandoned load's
  // finally skips its cleanup once superseded, so leaving the seq without
  // clearing loadingSources strands the spinner forever.
  const invalidateDeviceLoads = useCallback(() => {
    deviceLoadSeq.current += 1;
    setLoadingSources(false);
  }, []);
  const resetDeviceEditor = useCallback(() => {
    invalidateDeviceLoads();
    setShowDeviceForm(false);
    setDeviceDraft(emptyDraft());
    setDeviceSnapshot('');
  }, [invalidateDeviceLoads]);
  useEffect(() => {
    if (!isOpen) resetDeviceEditor();
  }, [isOpen, resetDeviceEditor]);
  useEffect(() => {
    resetDeviceEditor();
  }, [destinationId, resetDeviceEditor]);

  // The open request the editors were last primed for. Background route reloads
  // recreate currentRule's object identity; re-priming on those would throw away
  // whatever the user has typed into the open editor. The one exception is a saved
  // field mapping that finishes loading after the panel opened: it replaces the
  // blank form instead of being overwritten by it. A rule that blinks out during a
  // reload never re-primes, so a refetch can't wipe the user's edits.
  const primedForRef = useRef<{ request: string; withRule: boolean } | null>(
    null,
  );
  useEffect(() => {
    if (!isOpen) {
      primedForRef.current = null;
      setShowForm(false);
      return;
    }
    const fieldMapping = initialEditor === 'field_mapping';
    const request = `${destinationId}|${initialEditorNonce}`;
    const withRule = fieldMapping && !!currentRule;
    const primed = primedForRef.current;
    if (primed?.request === request && (primed.withRule || !withRule)) return;
    primedForRef.current = { request, withRule };
    // One editor at a time: asking for the field mapping drops the device draft,
    // the same way opening the device editor drops the field mapping form.
    if (fieldMapping) resetDeviceEditor();
    setShowDeviceForm(false);
    if (currentRule) {
      setStreamType(currentRule.streamType);
      setMapping(currentRule.mapping);
      if (currentConfigurationName) {
        // A saved name is treated as manually-set so we don't overwrite it when the
        // user edits destination_field afterwards.
        setConfigName(currentConfigurationName);
        setNameWasEdited(true);
      } else {
        // Legacy config without a name — fall back to the derived default.
        setConfigName(
          deriveDefaultName(
            providerId,
            currentRule.mapping.destination_field ?? '',
          ),
        );
        setNameWasEdited(false);
      }
    } else {
      setStreamType('ev');
      setMapping(emptyMapping);
      setConfigName(deriveDefaultName(providerId, ''));
      setNameWasEdited(false);
    }
    setMode('builder');
    setErrors({});
    // Clear stale validation from a previously-edited rule so Save isn't briefly
    // enabled on a different destination before the editor re-validates.
    setCodeValid(null);
    setResetTick((tick) => tick + 1);
    setSnapshot(null);
    // A destination holds ONE mapping, so a saved one is edited in place; a blank
    // form here would replace it on save without ever showing it.
    setShowForm(fieldMapping);
  }, [
    isOpen,
    currentRule,
    currentConfigurationName,
    destinationId,
    providerId,
    initialEditor,
    initialEditorNonce,
    resetDeviceEditor,
  ]);

  // Declared AFTER the reset effect above so it runs after it — the reset closes the
  // device editor, and this reopens it when the user clicked that rule's Add or Edit.
  // The field mapping form is opened by the reset itself.
  useEffect(() => {
    if (isOpen && initialEditor === 'device') openDeviceForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialEditorNonce]);

  // The route's source filters load separately, so Add Rule can be clicked before
  // a saved filter has arrived. When it lands, the open editor reloads it — a blank
  // draft saved over the existing rule would wipe its list.
  const deviceOpenedWithFilterRef = useRef(false);
  useEffect(() => {
    if (
      isOpen &&
      showDeviceForm &&
      currentSourceFilter &&
      !deviceOpenedWithFilterRef.current
    )
      openDeviceForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSourceFilter?.id]);

  // Snapshot the values that determine "is this dirty?" right as we enter the form. We
  // compare against this snapshot (and not currentRule directly) so it stays stable
  // regardless of how the user navigated into the form.
  useEffect(() => {
    if (!showForm) {
      setSnapshot(null);
      return;
    }
    setSnapshot({
      streamType,
      mapping: JSON.stringify(mapping),
      configName,
    });
    // We intentionally snapshot only when the form opens or is re-primed (resetTick)
    // — once inside, edits should mark the form as dirty rather than re-baselining.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showForm, resetTick]);

  useEffect(() => {
    if (mode === 'code' && destinationId && providerId) {
      setCode(buildJsonPreview(providerId, destinationId, streamType, mapping));
    }
  }, [mode, providerId, destinationId, streamType, mapping]);

  const builderValid = useMemo(() => {
    if ((mapping.destination_field ?? '').trim() === '') return false;
    // provider_field is only required when value mappings are populated — applies to
    // both Events and Observations now that value mapping is enabled for both.
    const mapHasEntries = !!mapping.map && Object.keys(mapping.map).length > 0;
    if (mapHasEntries) {
      return (mapping.provider_field ?? '').trim() !== '';
    }
    return true;
  }, [mapping.destination_field, mapping.provider_field, mapping.map]);

  const nameIsValid = configName.trim() !== '';

  // The form is "dirty" when the rule (per the active mode) or the name differ from the
  // snapshot taken at form-open. Save stays disabled when nothing changed so we don't
  // ping the backend (and create a duplicate route_configuration) on a no-op click.
  const isDirty = useMemo(() => {
    if (!snapshot) return false;
    if (configName !== snapshot.configName) return true;
    if (mode === 'builder') {
      if (streamType !== snapshot.streamType) return true;
      if (JSON.stringify(mapping) !== snapshot.mapping) return true;
      return false;
    }
    if (!codeValid) return false;
    if (codeValid.streamType !== snapshot.streamType) return true;
    return JSON.stringify(codeValid.mapping) !== snapshot.mapping;
  }, [snapshot, mode, streamType, mapping, codeValid, configName]);

  const ruleIsValid = mode === 'builder' ? builderValid : codeValid !== null;
  const canSave = ruleIsValid && nameIsValid && isDirty;

  // Note on form state: FieldMappingForm/FieldMappingCodeEditor are keyed by `resetTick`
  // and only mount while `showForm` is true. Leaving the form always closes the panel,
  // and every open request re-primes the form above, bumping `resetTick` and clearing
  // `codeValid` — so each time the form is shown it remounts fresh.

  // The snapshot is taken from the loaded draft, not from an empty one, so Save stays
  // disabled until the user actually changes something — a save with no edit would
  // rewrite the whole device list for nothing.
  const openDeviceForm = async () => {
    const seq = ++deviceLoadSeq.current;
    deviceOpenedWithFilterRef.current = !!currentSourceFilter;
    setShowForm(false);
    setShowDeviceForm(true);
    if (!currentSourceFilter) {
      // This call owns the generation now, so no superseded load will run
      // its cleanup — and this path starts no load of its own.
      setLoadingSources(false);
      const draft = emptyDraft();
      setDeviceDraft(draft);
      setDeviceSnapshot(JSON.stringify(draft));
      return;
    }
    setLoadingSources(true);
    try {
      const sources =
        (await onLoadSourceFilterSources?.(currentSourceFilter.id)) ?? [];
      // A newer open or a close/retarget owns the editor now; this response
      // belongs to a rule the user already left.
      if (seq !== deviceLoadSeq.current) return;
      const draft = draftFromFilter(currentSourceFilter, sources);
      setDeviceDraft(draft);
      setDeviceSnapshot(JSON.stringify(draft));
    } catch {
      if (seq !== deviceLoadSeq.current) return;
      // An incomplete draft saved wholesale would wipe the missing devices from
      // the rule — better no editor than a lying one.
      toast.error(
        `${t('Something went wrong. Please try again or contact support.')}`,
        { position: toast.POSITION.TOP_RIGHT },
      );
      onClose();
    } finally {
      if (seq === deviceLoadSeq.current) setLoadingSources(false);
    }
  };

  // Each rule kind is edited on its own, so leaving the editor closes the panel.
  const handleCancel = () => {
    onClose();
  };

  const handleSwitchMode = (next: Mode) => {
    // Switching code → builder: if the JSON is valid, carry its values into the builder
    // so the user doesn't silently lose what they typed in code mode.
    if (next === 'builder' && mode === 'code' && codeValid) {
      setStreamType(codeValid.streamType);
      setMapping(codeValid.mapping);
    }
    setMode(next);
  };

  // Resolves whether the save landed, so the leave-page prompt only moves on
  // after a real save.
  const handleSave = async (): Promise<boolean> => {
    if (saving) return false;
    const trimmedName = configName.trim();
    if (!trimmedName) return false;
    const runSave = async (
      saveStreamType: StreamType,
      saveMapping: FieldMapping,
    ): Promise<boolean> => {
      setSaving(true);
      try {
        const serverErrors = await onSave(
          saveStreamType,
          saveMapping,
          trimmedName,
        );
        // Backend rejected the payload — show its field errors on top of the form
        // so the user can correct them. We intentionally don't clear other errors:
        // setErrors is a full replacement so it stays in sync with the latest save.
        if (serverErrors && Object.keys(serverErrors).length > 0) {
          setErrors(serverErrors);
          return false;
        }
        setErrors({});
        return true;
      } catch {
        // The parent has already surfaced the failure; the form stays as it is.
        return false;
      } finally {
        setSaving(false);
      }
    };
    if (mode === 'builder') {
      const nextErrors: FormErrors = {};
      if ((mapping.destination_field ?? '').trim() === '')
        nextErrors.destination_field = t('Field to change');
      const mapHasEntries =
        !!mapping.map && Object.keys(mapping.map).length > 0;
      if (mapHasEntries && (mapping.provider_field ?? '').trim() === '') {
        nextErrors.provider_field = t('Source field for mapping');
      }
      setErrors(nextErrors);
      if (Object.keys(nextErrors).length > 0) return false;
      return runSave(streamType, mapping);
    }
    if (codeValid) return runSave(codeValid.streamType, codeValid.mapping);
    return false;
  };

  const deviceDirty = JSON.stringify(deviceDraft) !== deviceSnapshot;
  // An emptied BLACKLIST is savable when a rule exists: it means "delete the rule",
  // which is exactly the everything-reaches state the summary describes. An empty
  // whitelist stays unsavable — nothing would reach the destination.
  const emptyBlacklistDelete =
    deviceDraft.sources.length === 0 &&
    deviceDraft.mode === 'blacklist' &&
    !!currentSourceFilter;
  const canSaveDevices =
    deviceDirty &&
    !loadingSources &&
    (deviceDraft.sources.length > 0 || emptyBlacklistDelete);

  const performDeleteRule = async () => {
    if (saving) return;
    setSaving(true);
    try {
      // Closing is the parent's call, like every other save; hiding the editor
      // here would leave an empty panel whenever the parent doesn't close.
      await onDeleteSourceFilter?.();
    } catch {
      // The parent already surfaced the error; an unhandled rejection here would
      // be the only other trace of it.
    } finally {
      setSaving(false);
      setConfirmDeleteRule(false);
    }
  };

  const handleSaveDevices = async (): Promise<boolean> => {
    if (saving || !canSaveDevices) return false;
    if (emptyBlacklistDelete) {
      // An emptied blocklist saves as a DELETE - same consequence, same dialog.
      setConfirmDeleteRule(true);
      return false;
    }
    setSaving(true);
    try {
      await onSaveSourceFilter?.(deviceDraft);
      // Leaving the editor invalidates any load still in flight, as an
      // invariant rather than case-by-case reasoning.
      invalidateDeviceLoads();
      setShowDeviceForm(false);
      return true;
    } catch {
      // The parent has already surfaced the error; keep the editor open so the
      // user's device list isn't thrown away with it.
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Read at click time by the page's tab and link guards, never during render.
  if (pendingEditsRef) {
    pendingEditsRef.current = {
      hasEdits: () =>
        isOpen && ((showForm && isDirty) || (showDeviceForm && deviceDirty)),
      save: () => (showDeviceForm ? handleSaveDevices() : handleSave()),
      discard: onClose,
    };
  }

  const handleDeleteDevices = () => {
    if (saving) return;
    setConfirmDeleteRule(true);
  };

  // Cancel means "I'm done here", not "take me to the rule list" — the panel
  // closes. The open-reset effect restores the views on the next open.
  const handleCancelDevices = () => {
    onClose();
  };

  const handleDelete = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await onDelete();
    } finally {
      setSaving(false);
    }
  };

  // Below the md breakpoint (the top-nav hamburger threshold) the panel goes full-screen.
  // It sits below the mobile nav menu (z-59/60) but the floating "?" and Help panel stack
  // above it (z-57 / z-58) so help is reachable over the full-screen Rules panel.
  return (
    <div
      data-testid="TransformationRulesPanel-panel"
      className={`fixed right-0 top-0 z-[56] flex h-full w-full flex-col bg-white shadow-xl transition-all duration-300 md:z-[60] md:w-[520px] ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      }`}
      style={isResizing ? { transition: 'none' } : undefined}
      aria-hidden={!isOpen}
      {...(!isOpen &&
        ({ inert: '' } as unknown as React.HTMLAttributes<HTMLDivElement>))}
    >
      <div className="flex items-center justify-between border-b border-unlisted-gray-lines px-4 py-4">
        <div className="flex flex-col">
          <h2 className="text-lg font-semibold text-ui-text-primary-text">
            {t('Rules')}
          </h2>
          {destination && (
            <span className="truncate text-sm text-ui-text-primary-text">
              {destination.name}
            </span>
          )}
        </div>
        <button
          onClick={onClose}
          className="flex h-9 w-9 items-center justify-center text-ui-text-secondary-text hover:text-ui-text-primary-text"
          aria-label={t('Close')}
          data-testid="TransformationRulesPanel-close"
        >
          ✕
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4">
        {showDeviceForm ? (
          loadingSources ? (
            <span
              className="text-sm text-ui-text-secondary-text"
              data-testid="TransformationRulesPanel-device-loading"
            >
              {t('Loading sources…')}
            </span>
          ) : (
            <SourceFiltersForm
              providerId={providerId}
              destinationName={destination?.name ?? ''}
              value={deviceDraft}
              onChange={setDeviceDraft}
            />
          )
        ) : showForm ? (
          <div className="flex flex-col gap-4">
            <div className="rounded border border-unlisted-gray-lines bg-unlisted-very-light-gray p-4">
              <h3 className="mb-3 text-base font-semibold text-ui-text-primary-text">
                {t('Route Configuration')}
              </h3>
              <label
                className={formClasses.label}
                htmlFor="route-config-name-input"
              >
                {t('Name')}
              </label>
              <input
                id="route-config-name-input"
                data-testid="TransformationRulesPanel-config-name"
                className={formClasses.input}
                type="text"
                value={configName}
                onChange={(event) => {
                  setConfigName(event.target.value);
                  setNameWasEdited(true);
                }}
              />
            </div>
            <div className="rounded border border-unlisted-gray-lines bg-unlisted-very-light-gray">
              <div className="flex flex-row items-center justify-between p-4">
                <h3 className="text-base font-semibold text-ui-text-primary-text">
                  {t('Field Mapping')}
                </h3>
                <div
                  role="tablist"
                  aria-label={t('Field Mapping')}
                  className={toggleClasses.container}
                >
                  <button
                    type="button"
                    role="tab"
                    aria-selected={mode === 'builder'}
                    onClick={() => handleSwitchMode('builder')}
                    className={
                      mode === 'builder' ? segmentActive : segmentInactive
                    }
                    data-testid="TransformationRulesPanel-mode-builder"
                  >
                    {t('Builder')}
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={mode === 'code'}
                    onClick={() => handleSwitchMode('code')}
                    className={
                      mode === 'code' ? segmentActive : segmentInactive
                    }
                    data-testid="TransformationRulesPanel-mode-code"
                  >
                    {t('Code')}
                  </button>
                </div>
              </div>

              {mode === 'builder' ? (
                <FieldMappingForm
                  key={resetTick}
                  streamType={streamType}
                  mapping={mapping}
                  errors={errors}
                  onChange={({
                    streamType: nextStream,
                    mapping: nextMapping,
                  }) => {
                    setStreamType(nextStream);
                    setMapping(nextMapping);
                    // Auto-fill the configuration name from destination_field while the
                    // user hasn't manually edited it.
                    if (!nameWasEdited) {
                      setConfigName(
                        deriveDefaultName(
                          providerId,
                          nextMapping.destination_field ?? '',
                        ),
                      );
                    }
                  }}
                />
              ) : (
                <FieldMappingCodeEditor
                  key={resetTick}
                  value={code}
                  onChange={setCode}
                  providerId={providerId}
                  destinationId={destinationId}
                  onValidChange={setCodeValid}
                />
              )}
            </div>
          </div>
        ) : null}
      </div>

      {showDeviceForm && (
        <div className="flex flex-row items-center justify-between gap-2 border-t border-unlisted-gray-lines px-4 py-3">
          {/* Same footer shape as every other rule: Delete left, actions right.
                        The counts live in the form under the mode switch — a per-rule
                        footer variant fought Delete Rule for attention. */}
          <div className="flex min-w-0 items-center gap-3">
            {/* An emptied blocklist turns Save into Delete Rule; a second Delete
                beside it would offer the same action twice. */}
            {currentSourceFilter && !emptyBlacklistDelete && (
              <button
                type="button"
                onClick={handleDeleteDevices}
                disabled={saving}
                className={buttonClasses.destructiveSm}
                data-testid="TransformationRulesPanel-device-delete-footer"
              >
                {t('Delete Rule')}
              </button>
            )}
          </div>
          <div className="flex shrink-0 flex-row gap-3">
            <button
              type="button"
              onClick={handleCancelDevices}
              className={buttonClasses.cancelSm}
              data-testid="TransformationRulesPanel-device-cancel"
            >
              {t('Cancel')}
            </button>
            <button
              type="button"
              onClick={handleSaveDevices}
              disabled={!canSaveDevices || saving}
              className={
                emptyBlacklistDelete
                  ? buttonClasses.destructiveSm
                  : buttonClasses.primarySm
              }
              data-testid="TransformationRulesPanel-device-save"
            >
              {/* An emptied blacklist saves as a DELETE; a button that
                                says Save while destroying the rule hides the outcome. */}
              {emptyBlacklistDelete ? t('Delete Rule') : t('Save Rule')}
            </button>
          </div>
        </div>
      )}

      {showForm && (
        <div className="flex flex-row justify-between gap-2 border-t border-unlisted-gray-lines px-4 py-3">
          <div>
            {currentRule && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={saving}
                className={buttonClasses.destructiveSm}
                data-testid="TransformationRulesPanel-delete-footer"
              >
                {t('Delete Rule')}
              </button>
            )}
          </div>
          <div className="flex flex-row gap-3">
            <button
              type="button"
              onClick={handleCancel}
              className={buttonClasses.cancelSm}
              data-testid="TransformationRulesPanel-cancel"
            >
              {t('Cancel')}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!canSave || saving}
              className={buttonClasses.primarySm}
              data-testid="TransformationRulesPanel-save"
            >
              {t('Save Rule')}
            </button>
          </div>
        </div>
      )}

      {confirmDeleteRule && (
        <ModalShell
          title={t('Delete this rule?')}
          // Guarded while the DELETE is in flight (covers overlay, X and
          // Escape): the dialog must outlive the request so its outcome
          // is never silent.
          onClose={() => {
            if (!saving) setConfirmDeleteRule(false);
          }}
          footer={
            <>
              <button
                className={buttonClasses.destructiveMd}
                type="button"
                onClick={performDeleteRule}
                disabled={saving}
                data-testid="TransformationRulesPanel-confirm-delete-rule"
              >
                {t('Delete Rule')}
              </button>
              <button
                className={buttonClasses.cancelMd}
                type="button"
                onClick={() => setConfirmDeleteRule(false)}
                disabled={saving}
                data-testid="TransformationRulesPanel-cancel-delete-rule"
              >
                {t('Cancel')}
              </button>
            </>
          }
        >
          <p
            className="break-words text-sm text-ui-text-primary-text"
            data-testid="TransformationRulesPanel-delete-dialog-text"
          >
            {(currentSourceFilter?.mode ?? deviceDraft.mode) === 'whitelist'
              ? t(
                  'Deleting this rule removes the restriction: every source from this provider, including new ones, will reach {{name}} again.',
                  { name: destination?.name ?? '' },
                )
              : t(
                  'Deleting this rule unblocks its sources: everything from this provider will reach {{name}}.',
                  { name: destination?.name ?? '' },
                )}
          </p>
        </ModalShell>
      )}
    </div>
  );
};

export default TransformationRulesPanel;
